import { computed, unref } from 'vue'
import { useModernizr } from './useModernizr.js'
import { useWindowSize, breakpointsTailwind } from '@vueuse/core'

/**
 * @typedef {Object} MediaFile
 * @property {string} id
 * @property {string} metaData
 * @property {string} mimeType
 */

/**
 * @typedef {Object} FocalPoint
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {Object} OptimizedImageParams
 * @property {'webp'|'png'|'jpg'} format
 * @property {number} width
 * @property {number} height
 * @property {number|'breakpoint'} maxWidth
 * @property {number} maxHeight
 * @property {number} quality
 * @property {string} aspectRatio
 * @property {FIT_CLIP|FIT_CROP|FIT_FILL|FIT_MAX|FIT_MIN|FIT_SCALE} fit
 * @property {string} fillColor
 * @property {CROP_BOTTOM_LEFT|CROP_BOTTOM_RIGHT|CROP_ENTROPY|CROP_FOCALPOINT|CROP_TOP_LEFT|CROP_TOP_RIGHT} crop
 * @property {FocalPoint} focalPoint
 */

/**
 * @type OptimizedImageParams
 */
export class OptimizedImageParams {}

export const FIT_FILL = 'fill'
export const FIT_SCALE = 'scale'
export const FIT_CROP = 'crop'
export const FIT_CLIP = 'clip'
export const FIT_MIN = 'min'
export const FIT_MAX = 'max'

export const CROP_FOCALPOINT = 'focalpoint'
export const CROP_ENTROPY = 'entropy'

export const CROP_TOP_LEFT = 'top,left'
export const CROP_TOP_RIGHT = 'top,right'
export const CROP_BOTTOM_LEFT = 'bottom,left'
export const CROP_BOTTOM_RIGHT = 'bottom,right'

export const cropTypes = [CROP_FOCALPOINT, CROP_ENTROPY, CROP_TOP_LEFT, CROP_TOP_RIGHT, CROP_BOTTOM_LEFT, CROP_BOTTOM_RIGHT]

export const fitTypes = [FIT_FILL, FIT_SCALE, FIT_CROP, FIT_CLIP, FIT_MIN, FIT_MAX]

const metadataCache = {}
/**
 *
 * @param {MediaFile} mediaFile
 * @returns {Object} MediaFile decoded metadata
 */
const getMetaData = (mediaFile) => {
  if (!mediaFile) throw new Error('A MediaFile must be supplied')
  if (!mediaFile.metaData) throw new Error('"metaData" field must be included in the query for the mediaFile')

  if (!metadataCache[mediaFile.id]) {
    metadataCache[mediaFile.id] = JSON.parse(mediaFile.metaData)
  }
  return metadataCache[mediaFile.id]
}
/**
 *
 * @param {MediaFile} mediaFile
 * @returns {string} A Base64 image that can be used as preview of the real image
 */
const getLazySrc = (mediaFile) => {
  return getMetaData(mediaFile)['image-lazy-src']
}
/**
 *
 * @param {MediaFile} mediaFile
 * @returns {number} width of the original image
 */
const getWidth = (mediaFile) => {
  const metaData = getMetaData(mediaFile)
  return parseInt(metaData['image-width'])
}
/**
 *
 * @param {MediaFile} mediaFile
 * @returns {number} height of the original image
 */
const getHeight = (mediaFile) => {
  const metaData = getMetaData(mediaFile)
  return parseInt(metaData['image-height'])
}
/**
 *
 * @param {MediaFile} mediaFile
 * @returns {number} Aspect ratio of the original image
 */
const getAspectRatio = (mediaFile) => {
  return getWidth(mediaFile) / getHeight(mediaFile)
}
/**
 *
 * @param {MediaFile} mediaFile
 * @param {OptimizedImageParams} params
 * @param {number} windowWidth
 * @returns {string}
 */
const optimizedImageURL = (mediaFile, { format, width, height, maxWidth, maxHeight, quality, aspectRatio, fit, fillColor = 'black', crop, focalPoint = { x: 0.5, y: 0.5 }, blur } = {}, windowWidth) => {
  if (!mediaFile) { throw new Error('A MediaFile must be supplied') }

  // let host = 'https://' + DOT_ENV.SERVICE_TARGET_DOMAIN + DOT_ENV.OPTIMIZED_IMAGE_API_ENDPOINT
  const host = ''

  let base = host + DOT_ENV.OPTIMIZED_IMAGE_API_ENDPOINT + '/' + mediaFile.id + '?auto=compress'

  if (DOT_ENV.IS_OFFLINE) {
    base = DOT_ENV.API_ENDPOINT + DOT_ENV.SERVICE_STAGE + DOT_ENV.OPTIMIZED_IMAGE_API_ENDPOINT + '/' + mediaFile.id + '?auto=compress'
  }

  if ((width && height && !fit) || (aspectRatio && !fit)) {
    // default to fit=crop when user expects
    // exact sizes
    fit = FIT_CROP
  }

  if (!width && maxWidth === 'breakpoint') {
    const breakpoints = Object.values(breakpointsTailwind).concat([0, 4096])
    // sort into ascending order, for example
    //
    // 0: 0
    // 1: 640
    // 2: 768
    // 3: 1024
    // 4: 1280
    // 5: 1536
    // 6: 4096
    breakpoints.sort((a, b) => a > b ? 1 : -1)
    // find the one which is greatest than our current resolution
    const greatest = breakpoints.find(b => windowWidth.value < b)
    if (greatest !== null && greatest !== undefined) {
      maxWidth = greatest * window.devicePixelRatio
    }
  }
  if (aspectRatio) {
    base += `&ar=${aspectRatio}`
    // always have a fixed width when aspectRatio is required
    if (maxWidth && !width) {
      width = maxWidth
    }
    // Default to image width
    if (!width && !maxWidth) {
      width = getWidth(mediaFile)
    }
  }
  if (width) base += `&w=${Math.round(Math.max(1, width))}`
  if (height) base += `&h=${Math.round(Math.max(1, height))}`

  if (!width && maxWidth) {
    const width = getWidth(mediaFile)
    if (width > maxWidth) base += `&w=${Math.round(Math.max(1, maxWidth))}`
  }

  if (!height && maxHeight) {
    const height = getHeight(mediaFile)
    if (height > maxHeight) base += `&h=${Math.round(Math.max(1, maxHeight))}`
  }

  if (quality) base += `&q=${Math.round(Math.max(1, Math.min(100, quality)))}`
  if (blur) base += `&blur=${Math.max(0, Math.min(2000, Math.round(blur)))}`

  if (fit && fitTypes.indexOf(fit) !== -1) {
    base += `&fit=${fit}`

    if (fit === FIT_FILL) {
      base += `&fill-color=${fillColor}`
    }
  }

  if (crop && cropTypes.indexOf(crop) !== -1) {
    base += `&crop=${crop}`

    if (crop === CROP_FOCALPOINT) {
      base += `&fp-x=${Math.max(0, Math.min(1, focalPoint.x))}`
      base += `&fp-y=${Math.max(0, Math.min(1, focalPoint.y))}`
    }
  }

  if (format) {
    // manually set format overrides everything
    base += `&fm=${format}`
  } else {
    const modernizr = useModernizr()
    // auto format to webp if certains conditions are met
    const hasWebpSupport = !!modernizr.webp // browser must support webp!
    const isGif = mediaFile.mimeType === 'image/gif' || mediaFile.id.indexOf('.gif') !== -1 // gifs to webp don't translate well
    const isSvg = mediaFile.mimeType === 'image/svg+xml' || mediaFile.id.indexOf('.svg') !== -1 // svg to webp are not optimal
    const isWebp = mediaFile.mimeType === 'image/webp' || mediaFile.id.indexOf('.webp') !== -1 // image is already a webp
    const isPng = mediaFile.mimeType === 'image/png' || mediaFile.id.indexOf('.png') !== -1 // we need to check support for transparent webp
    const hasWebpAlphaSupport = modernizr['webp-alpha']

    if (hasWebpSupport && !isGif && !isSvg && !isWebp && (!isPng || (isPng && hasWebpAlphaSupport))) {
      base += '&fm=webp'
    }
  }
  return base
}

/**
 *
 * @param {MediaFile} mediaFile
 * @param {OptimizedImageParams} params
 * @returns
 */
export const useOptimizedImage = (mediaFile, params) => {
  const { width: windowWidth } = useWindowSize()
  // console.log('useoptimizedimage')
  return {
    url: computed(() => optimizedImageURL(mediaFile, unref(params), windowWidth)),
    metaData: computed(() => getMetaData(mediaFile)),
    aspectRatio: computed(() => getAspectRatio(mediaFile)),
    width: computed(() => getWidth(mediaFile)),
    height: computed(() => getHeight(mediaFile)),
    lazySrc: computed(() => getLazySrc(mediaFile))
  }
}

export default useOptimizedImage
